/** CRM lead treatment statuses. `no_answer` = אין מענה (call attempt, lead stays open). */
export const CRM_LEAD_STATUS_OPTIONS = [
  { value: "new", label: "חדש" },
  { value: "in_progress", label: "בטיפול" },
  { value: "no_answer", label: "אין מענה" },
  { value: "closed", label: "נסגר" },
  { value: "not_relevant", label: "לא רלוונטי" },
];

export function formatPublicLeadCategory(category) {
  const value = String(category || "").trim();
  if (!value) return "כללי";
  if (value === "campaign_bank_transfer" || value === "belvedere_campaign") return "קמפיין בלוודר";
  if (
    value === "dynamic_package_public_offer" ||
    value === "public_offer"
  ) {
    return "הצעה פומבית";
  }
  if (value === "package_compare") return "השוואת חבילות";
  if (value === "hotel_compare") return "השוואת מלונות";
  if (value === "manual") return "ידני";
  if (value === "הזמנה" || value === "completed_order") return "הזמנה";
  if (value === "פנייה כללית" || value === "ops_request") return "פנייה כללית";
  if (value === "meta_lead_ads" || value.toLowerCase() === "meta") return "Meta Ads";
  return value;
}

/** Parse public_site_leads.meta_ad_data (object or JSON string). */
export function getMetaAdData(row) {
  const raw = row?.meta_ad_data;
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function isMetaLeadRow(row) {
  if (!row || typeof row !== "object") return false;
  const sourceType = String(row.source_type || "").trim().toUpperCase();
  if (sourceType === "META" || sourceType === "META_LEAD_ADS") return true;
  const category = String(row.category || "").trim().toLowerCase();
  if (category === "meta_lead_ads") return true;
  const sourceKey = String(row.source_key || "").trim().toLowerCase();
  if (sourceKey.startsWith("meta_leadgen:")) return true;
  const siteId = String(row.agent_public_site_id || "").trim().toLowerCase();
  return siteId === "meta-lead-ads";
}

/** Campaign / ad label for Meta leads (empty when organic or missing). */
export function formatMetaLeadCampaign(row) {
  const ad = getMetaAdData(row);
  if (!ad) return "";
  const campaign = String(ad.campaign_name || "").trim();
  if (campaign) return campaign;
  const adName = String(ad.ad_name || "").trim();
  if (adName) return adName;
  const adset = String(ad.adset_name || "").trim();
  return adset || "";
}

/** Completed booking customer file (דוקט), vs prospect lead. */
export function isCrmDocketLead(row) {
  if (!row || typeof row !== "object") return false;
  const sourceType = String(row.source_type || "").trim().toLowerCase();
  if (sourceType === "completed_order") return true;
  if (row.linked_booking) return true;
  const category = String(row.category || "").trim();
  if (
    category === "הזמנה" ||
    category === "completed_order" ||
    category === "פנייה כללית"
  ) {
    return true;
  }
  const siteId = String(row.agent_public_site_id || "").trim().toLowerCase();
  if (siteId.startsWith("completed-order")) return true;
  const sourceKey = String(row.source_key || "").trim().toLowerCase();
  if (sourceKey.startsWith("ops_request:")) return true;
  const sourceUrl = String(row.source_url || "");
  if (/orderId=/i.test(sourceUrl) || /opsRequestId=/i.test(sourceUrl)) return true;
  const blob = [
    row.ai_sales_note,
    row.message,
    row.agent_internal_note,
  ]
    .map((v) => String(v || ""))
    .join("\n");
  if (
    blob.includes("הזמנה אוטומטית") ||
    blob.includes("הזמנה שבוצעה במערכת") ||
    blob.includes("תיק נפתח אוטומטית מהזמנה") ||
    blob.includes("תיק נפתח אוטומטית מפנייה לאופרציה") ||
    /order_id:\s*[0-9a-f-]{36}/i.test(blob) ||
    /lds_orders:[0-9a-f-]{36}/i.test(blob) ||
    /ops_request_id:\s*[0-9a-f-]{36}/i.test(blob)
  ) {
    return true;
  }
  return false;
}

/** Match CRM Leads tab: blank/missing status is treated as untreated ("new"). */
export function isCrmLeadStatusNew(lead) {
  return (lead?.status || "new") === "new";
}

/** Top-bar "new leads": still in the admin pool — not assigned to an agent. */
export function isCrmLeadUnassigned(lead) {
  const assignment = String(lead?.assignment_status || "").trim().toLowerCase();
  if (assignment === "unassigned") return true;
  if (assignment === "assigned") return false;
  const raw = lead?.agent_id ?? lead?.agentId;
  if (raw == null || raw === "") return true;
  const n = Number(raw);
  return !Number.isFinite(n) || n <= 0;
}

/**
 * Top-bar "N לידים חדשים": unassigned prospect leads only
 * (not dockets, status new/blank, no agent assigned).
 * Assigned leads drop out of the count. Meta / Facebook Lead Ads in the
 * admin pool (agent_id NULL) are included until they are assigned.
 */
export function countNewCrmProspectLeads(leads) {
  if (!Array.isArray(leads)) return 0;
  return leads.filter(
    (lead) =>
      !isCrmDocketLead(lead) &&
      isCrmLeadStatusNew(lead) &&
      isCrmLeadUnassigned(lead)
  ).length;
}

export function formatLeadSourceType(row) {
  const sourceType = String(row?.source_type || "").trim();
  if (sourceType === "manual") return { label: "ידני", className: "crm-badge--manual" };
  if (isCrmDocketLead(row)) {
    return { label: "הזמנה", className: "crm-badge--order" };
  }
  if (sourceType === "company_landing_page") {
    return { label: "דף נחיתה", className: "crm-badge--landing" };
  }
  if (isMetaLeadRow(row)) {
    const campaign = formatMetaLeadCampaign(row);
    return {
      label: "Meta",
      className: "crm-badge--meta",
      detail: campaign || null,
    };
  }
  const siteId = String(row?.agent_public_site_id || "").trim().toLowerCase();
  const sourceUrl = String(row?.source_url || "");
  const category = String(row?.category || "").trim();
  if (
    siteId.startsWith("public-offer::") ||
    /\/offers\/public\//i.test(sourceUrl) ||
    category === "הצעה פומבית" ||
    category === "dynamic_package_public_offer" ||
    category === "public_offer"
  ) {
    return { label: "הצעה פומבית", className: "crm-badge--source" };
  }
  if (row?.source_url) return { label: "אתר", className: "crm-badge--source" };
  return { label: "אתר / אחר", className: "crm-badge--source" };
}
